import { useState } from "react";

import { FilterPopover } from "src/base-components/FilterPopover/FilterPopover";
import {
  ConditionRenderContext,
  OperatorOption,
} from "src/base-components/FilterPopover/types";
import { StringValueRenderer } from "src/base-components/FilterPopover/ValueRenderers";
import { AssigneeValueRenderer } from "src/cases/queues/AssigneeValueRenderer";
import { EntityRefFilterValueRenderer } from "src/cases/queues/EntityRefFilterValueRenderer";
import { useCasesQueueParams } from "src/cases/queues/hooks/useCasesQueueParams";
import { useFilterFields } from "src/cases/queues/hooks/useFilterFields";
import { RefValueRenderer } from "src/cases/queues/RefValueRenderer";
import { CasesPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const EQ_OPERATOR: OperatorOption = {
  key: "eq",
  value: { description: "Equal to", code: "==" },
};

const NUMERIC_OPERATORS: OperatorOption[] = [
  EQ_OPERATOR,
  { key: "gt", value: { description: "Greater than", code: ">" } },
  {
    key: "gte",
    value: { description: "Greater than or equal to", code: ">=" },
  },
  { key: "lt", value: { description: "Less than", code: "<" } },
  { key: "lte", value: { description: "Less than or equal to", code: "<=" } },
];

const CONTAINS_OPERATOR: OperatorOption = {
  key: "contains",
  value: { description: "Contains", code: "contains" },
};

const OPERATOR_OPTIONS: Record<string, OperatorOption[]> = {
  string: [EQ_OPERATOR, CONTAINS_OPERATOR],
  url: [EQ_OPERATOR, CONTAINS_OPERATOR],
  number: NUMERIC_OPERATORS,
  integer: NUMERIC_OPERATORS,
  datetime: [EQ_OPERATOR],
  enum: [{ key: "one_of", value: { description: "One of", code: "one of" } }],
  tags: [
    CONTAINS_OPERATOR,
    { key: "one_of", value: { description: "One of", code: "one of" } },
  ],
  entity_ref: [EQ_OPERATOR],
  event_ref: [EQ_OPERATOR],
  uuid: [EQ_OPERATOR],
  assignee: [EQ_OPERATOR],
};

const VALUE_RENDERERS: Record<string, React.FC<ConditionRenderContext>> = {
  entity_ref: EntityRefFilterValueRenderer,
  event_ref: RefValueRenderer,
  uuid: RefValueRenderer,
  assignee: AssigneeValueRenderer,
  url: StringValueRenderer,
};

export const CasesFilterPopover: React.FC = () => {
  const { queue_id: queueId } = useParamsDecode<CasesPageParams>();
  const { fields, isLoading } = useFilterFields(queueId);
  const { filterFormData, hasActiveFilter, applyFilters } = useCasesQueueParams(
    queueId,
    fields,
  );
  const [isOpen, setIsOpen] = useState(false);

  return (
    <FilterPopover
      dataLoc="cases-filter-popover"
      defaultValues={filterFormData}
      fields={fields}
      hasActiveFilter={hasActiveFilter}
      isLoading={isLoading}
      isOpen={isOpen}
      operatorOptions={OPERATOR_OPTIONS}
      valueRenderers={VALUE_RENDERERS}
      onApply={applyFilters}
      onOpenChange={setIsOpen}
      allowedJunctions={["and"]}
      maxGroups={1}
      renderHeader="Additional filters"
    />
  );
};
